import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Button, Progress, List, Avatar, Tag, Spin, Alert, Empty, Skeleton, Space, Radio, Modal, Input, message } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ArrowUpOutlined,
  SettingOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  TransactionOutlined,
  WalletOutlined,
  CalendarOutlined,
  ShopOutlined,
  BellOutlined,
  FileProtectOutlined,
  PercentageOutlined,
  SearchOutlined,
  ReloadOutlined,
  CommentOutlined,
  ExclamationCircleOutlined,
  FileImageOutlined,
  AudioOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [generatedImageData, setGeneratedImageData] = useState([]);
  const [convertedAudioData, setConvertedAudioData] = useState([]);
  const [generatedVideoData, setGeneratedVideoData] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [recentIssues, setRecentIssues] = useState([]);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [resolveType, setResolveType] = useState(''); // 'feedback' or 'issue'
  const [timeScale, setTimeScale] = useState('daily'); // 'daily', 'weekly', 'monthly'
  const [revenueTimeScale, setRevenueTimeScale] = useState('daily');
  const [userActivityTimeScale, setUserActivityTimeScale] = useState('daily');
  const [contentTimeScale, setContentTimeScale] = useState('daily');
  
  // Dashboard metrics
  const [dashboardMetrics, setDashboardMetrics] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    totalTransactions: 0,
    totalRevenue: 0,
    totalCoinsUsed: 0,
    totalImages: 0,
    totalAudios: 0,
    totalVideos: 0,
    growthRate: 8.5, // Placeholder for growth calculation
  });

  // Chart states
  const [revenueChartData, setRevenueChartData] = useState({
    categories: [],
    series: [{
      name: 'Revenue',
      data: []
    }]
  });

  const [userActivityChartData, setUserActivityChartData] = useState({
    series: [
      {
        name: 'New Users',
        data: []
      },
      {
        name: 'Active Users',
        data: []
      }
    ],
    categories: []
  });

  const [subscriptionDistributionData, setSubscriptionDistributionData] = useState({
    labels: ['Yearly', 'Addon'],
    series: [0, 0]
  });

  const [transactionTypeData, setTransactionTypeData] = useState({
    labels: [],
    series: []
  });

  const [contentGenerationData, setContentGenerationData] = useState({
    categories: [],
    series: [
      {
        name: 'Images',
        data: []
      },
      {
        name: 'Audios',
        data: []
      }
    ]
  });

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use Promise.allSettled instead of Promise.all to handle partial failures
        const results = await Promise.allSettled([
          fetchUsers(),
          fetchOrders(),
          fetchTransactions(),
          fetchGeneratedImages(),
          fetchConvertedAudios(),
          fetchGeneratedVideos(),
          fetchRecentFeedback(),
          fetchRecentIssues()
        ]);
        
        // Check if any critical promises failed
        const criticalFailures = results.slice(0, 3).filter(r => r.status === 'rejected');
        if (criticalFailures.length > 0) {
          console.warn('Some critical data fetching operations failed:', 
            criticalFailures.map(f => f.reason?.message || 'Unknown error'));
          setError('Some dashboard data could not be loaded. Please refresh to try again.');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    if (userData.length > 0 && orderData.length > 0 && transactionData.length > 0 && 
        generatedImageData.length > 0 && convertedAudioData.length > 0 && generatedVideoData.length > 0) {
      calculateDashboardMetrics();
      prepareChartData();
      prepareRecentUsers();
    }
  }, [userData, orderData, transactionData, generatedImageData, convertedAudioData, generatedVideoData, revenueTimeScale, userActivityTimeScale, contentTimeScale]);

  // Add a utility function for retrying API calls
  const fetchWithRetry = async (url, options = {}, retries = 2, delay = 1000) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (retries <= 0) {
        throw error;
      }
      console.log(`Retrying fetch to ${url}. Attempts remaining: ${retries}`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(url, options, retries - 1, delay);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/fetchUserInfoAdmin', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Check if data has a nested data array structure
      let usersArray;
      if (data.success && Array.isArray(data.data)) {
        usersArray = data.data;
      } else if (Array.isArray(data)) {
        usersArray = data;
      } else {
        console.error('Data is not in expected format:', data);
        setUserData([]);
        return [];
      }
      
      setUserData(usersArray);
      return usersArray;
    } catch (err) {
      console.error('Error fetching users:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setUserData([]);
      return [];
    }
  };

  const fetchOrders = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllOrders', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Add this to include cookies in the request
      });
      
      if (data.success) {
        const processedOrders = processOrdersData(data.data);
        setOrderData(processedOrders);
        return processedOrders;
      } else if (data.data) {
        // Handle case where success flag might be missing but data is present
        const processedOrders = processOrdersData(data.data);
        setOrderData(processedOrders);
        return processedOrders;
      } else if (Array.isArray(data)) {
        // Handle case where the API directly returns an array
        const processedOrders = processOrdersData(data);
        setOrderData(processedOrders);
        return processedOrders;
      } else {
        throw new Error('Failed to fetch orders data: Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setOrderData([]);
      return [];
    }
  };

  const fetchTransactions = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllTransactions', {
        credentials: 'include'
      });
      
      if (data.success) {
        const processedTransactions = processTransactionsData(data.data);
        setTransactionData(processedTransactions);
        return processedTransactions;
      } else if (data.data) {
        // Handle case where success flag might be missing but data is present
        const processedTransactions = processTransactionsData(data.data);
        setTransactionData(processedTransactions);
        return processedTransactions;
      } else if (Array.isArray(data)) {
        // Handle case where the API directly returns an array
        const processedTransactions = processTransactionsData(data);
        setTransactionData(processedTransactions);
        return processedTransactions;
      } else {
        throw new Error('Failed to fetch transactions data: Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setTransactionData([]);
      return [];
    }
  };

  const fetchGeneratedImages = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllGeneratedImage', {
        credentials: 'include'
      });
      
      // Check if data has a nested data array structure
      let imagesArray;
      if (data.success && Array.isArray(data.data)) {
        imagesArray = data.data;
      } else if (Array.isArray(data)) {
        imagesArray = data;
      } else {
        console.error('Generated images data is not in expected format:', data);
        setGeneratedImageData([]);
        return [];
      }
      
      const processedImages = processImageData(imagesArray);
      setGeneratedImageData(processedImages);
      return processedImages;
    } catch (err) {
      console.error('Error fetching generated images:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setGeneratedImageData([]);
      return [];
    }
  };

  const fetchConvertedAudios = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllAudioConverted', {
        credentials: 'include'
      });
      
      // Check if data has a nested data array structure
      let audiosArray;
      if (data.success && Array.isArray(data.data)) {
        audiosArray = data.data;
      } else if (Array.isArray(data)) {
        audiosArray = data;
      } else {
        console.error('Converted audios data is not in expected format:', data);
        setConvertedAudioData([]);
        return [];
      }
      
      const processedAudios = processAudioData(audiosArray);
      setConvertedAudioData(processedAudios);
      return processedAudios;
    } catch (err) {
      console.error('Error fetching converted audios:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setConvertedAudioData([]);
      return [];
    }
  };

  const fetchGeneratedVideos = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllGeneratedVideo', {
        credentials: 'include'
      });
      
      // Check if data has a nested data array structure
      let videosArray;
      if (data.success && Array.isArray(data.data)) {
        videosArray = data.data;
      } else if (Array.isArray(data)) {
        videosArray = data;
      } else {
        console.error('Generated videos data is not in expected format:', data);
        setGeneratedVideoData([]);
        return [];
      }
      
      const processedVideos = processVideoData(videosArray);
      setGeneratedVideoData(processedVideos);
      return processedVideos;
    } catch (err) {
      console.error('Error fetching generated videos:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setGeneratedVideoData([]);
      return [];
    }
  };

  const fetchRecentFeedback = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllFeedback', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Extract feedback items from the nested structure (similar to Feedback.js)
      let allFeedback = [];
      if (Array.isArray(data)) {
        data.forEach(userGroup => {
          if (userGroup.feedback && Array.isArray(userGroup.feedback)) {
            const userFeedback = userGroup.feedback.map(feedback => ({
              ...feedback,
              user: userGroup.user, // Add user info to each feedback item
              featured: feedback.featured || false,
              resolved: feedback.status === 'resolved'
            }));
            allFeedback = allFeedback.concat(userFeedback);
          }
        });
      } else {
        console.error('Feedback data is not in expected format:', data);
        setRecentFeedback([]);
        return [];
      }
      
      // Sort by date and take most recent 3
      const sortedFeedback = [...allFeedback].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      }).slice(0, 3);
      
      setRecentFeedback(sortedFeedback);
      return sortedFeedback;
    } catch (err) {
      console.error('Error fetching recent feedback:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setRecentFeedback([]);
      return [];
    }
  };

  const fetchRecentIssues = async () => {
    try {
      const data = await fetchWithRetry('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllHelp', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Extract help requests from the nested structure (similar to Issues.js)
      let allIssues = [];
      if (Array.isArray(data)) {
        data.forEach(userGroup => {
          if (userGroup.helpRequests && Array.isArray(userGroup.helpRequests)) {
            const userIssues = userGroup.helpRequests.map(issue => ({
              ...issue,
              user: userGroup.user, // Add user info to each issue
              resolved: issue.status === 'resolved'
            }));
            allIssues = allIssues.concat(userIssues);
          }
        });
      } else {
        console.error('Issues data is not in expected format:', data);
        setRecentIssues([]);
        return [];
      }
      
      // Sort by date and take most recent 2
      const sortedIssues = [...allIssues].sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      }).slice(0, 2);
      
      setRecentIssues(sortedIssues);
      return sortedIssues;
    } catch (err) {
      console.error('Error fetching recent issues:', err);
      // Set empty array instead of throwing to prevent cascading errors
      setRecentIssues([]);
      return [];
    }
  };

  const handleResolveFeedback = async (feedbackId, adminComment) => {
    try {
      const userUid = localStorage.getItem('userUid');
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/resolveFeedback', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedbackId,
          adminComment,
          adminId: userUid
        }),
      });

      if (response.ok) {
        message.success('Feedback resolved successfully');
        fetchRecentFeedback(); // Refresh the list
      } else {
        message.error('Failed to resolve feedback');
      }
    } catch (error) {
      console.error('Error resolving feedback:', error);
      message.error('Error resolving feedback');
    }
  };

  const handleResolveHelpRequest = async (helpRequestId, adminComment) => {
    try {
      const userUid = localStorage.getItem('userUid');
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/resolveHelpRequest', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          helpRequestId,
          adminComment,
          adminId: userUid
        }),
      });

      if (response.ok) {
        message.success('Help request resolved successfully');
        fetchRecentIssues(); // Refresh the list
      } else {
        message.error('Failed to resolve help request');
      }
    } catch (error) {
      console.error('Error resolving help request:', error);
      message.error('Error resolving help request');
    }
  };

  const showResolveModal = (item, type) => {
    setSelectedItem(item);
    setResolveType(type);
    setResolveModalVisible(true);
  };

  const handleResolveSubmit = async () => {
    if (!adminComment.trim()) {
      message.warning('Please enter an admin comment');
      return;
    }

    if (resolveType === 'feedback') {
      await handleResolveFeedback(selectedItem.id, adminComment);
    } else if (resolveType === 'issue') {
      await handleResolveHelpRequest(selectedItem.id, adminComment);
    }

    setResolveModalVisible(false);
    setSelectedItem(null);
    setAdminComment('');
    setResolveType('');
  };

  const handleResolveCancel = () => {
    setResolveModalVisible(false);
    setSelectedItem(null);
    setAdminComment('');
    setResolveType('');
  };

  const processOrdersData = (usersData) => {
    let allOrders = [];
    
    usersData.forEach(userData => {
      const { user, orders } = userData;
      
      if (orders && orders.length > 0) {
        orders.forEach(order => {
          allOrders.push({
            ...order,
            user_name: user.name,
            user_email: user.email,
            user_uid: user.uid,
          });
        });
      }
    });
    
    return allOrders;
  };

  const processTransactionsData = (usersData) => {
    let allTransactions = [];
    
    usersData.forEach(userData => {
      const { user, transactions } = userData;
      
      if (transactions && transactions.length > 0) {
        transactions.forEach(transaction => {
          allTransactions.push({
            ...transaction,
            user_name: user.name,
            user_email: user.email,
            user_uid: user.uid,
          });
        });
      }
    });
    
    return allTransactions;
  };

  const processImageData = (usersData) => {
    let allImages = [];
    
    usersData.forEach(userData => {
      const { user, images } = userData;
      
      if (images && images.length > 0) {
        images.forEach(image => {
          allImages.push({
            ...image,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${image.image_id}`
          });
        });
      }
    });
    
    return allImages;
  };

  const processAudioData = (usersData) => {
    let allAudios = [];
    
    usersData.forEach(userData => {
      const { user, audios } = userData;
      
      if (audios && audios.length > 0) {
        audios.forEach(audio => {
          allAudios.push({
            ...audio,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${audio.audioid}`
          });
        });
      }
    });
    
    return allAudios;
  };

  const processVideoData = (usersData) => {
    let allVideos = [];
    
    usersData.forEach(userData => {
      const { user, videos } = userData;
      
      if (videos && videos.length > 0) {
        videos.forEach(video => {
          allVideos.push({
            ...video,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${video.video_id}`
          });
        });
      }
    });
    
    return allVideos;
  };

  const calculateDashboardMetrics = () => {
    const totalUsers = userData.length;
    const activeSubscriptions = orderData.filter(order => order.status === 'active').length;
    const totalTransactions = transactionData.length;
    const totalRevenue = orderData.reduce((sum, order) => sum + order.total_price, 0);
    const totalCoinsUsed = transactionData.reduce((sum, txn) => sum + txn.coin_amount, 0);
    const totalImages = generatedImageData.length;
    const totalAudios = convertedAudioData.length;
    const totalVideos = generatedVideoData.length;

    setDashboardMetrics({
      totalUsers,
      activeSubscriptions,
      totalTransactions,
      totalRevenue,
      totalCoinsUsed,
      totalImages,
      totalAudios,
      totalVideos,
      growthRate: 8.5 // Placeholder
    });
  };

  const prepareChartData = () => {
    // Process Revenue Chart Data based on revenueTimeScale
    processRevenueChartData();
    
    // Process User Activity Chart Data based on userActivityTimeScale
    processUserActivityChartData();
    
    // Process Content Generation Chart Data based on contentTimeScale
    processContentGenerationChartData();
    
    // Process subscription and transaction type data (no time scale needed)
    const yearlyPlans = orderData.filter(order => order.plan_name === 'Yearly').length;
    const addonPlans = orderData.filter(order => order.plan_name === 'Addon').length;
    
    setSubscriptionDistributionData({
      labels: ['Yearly', 'Addon'],
      series: [yearlyPlans, addonPlans]
    });

    const transactionTypes = {};
    transactionData.forEach(txn => {
      const type = txn.transaction_name || 'Unknown';
      if (transactionTypes[type]) {
        transactionTypes[type]++;
      } else {
        transactionTypes[type] = 1;
      }
    });

    setTransactionTypeData({
      labels: Object.keys(transactionTypes),
      series: Object.values(transactionTypes)
    });
  };

  const processRevenueChartData = () => {
    // Time scale calculations based on selected scale
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(revenueTimeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Revenue by time period
    const periodRevenue = {};
    
    if (revenueTimeScale === 'daily') {
      // Initialize last X days
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        periodRevenue[dateStr] = 0;
      }
    } else if (revenueTimeScale === 'weekly') {
      // Initialize last X weeks
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        periodRevenue[dateStr] = 0;
      }
    } else {
      // Monthly (default)
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        periodRevenue[monthYear] = 0;
      }
    }

    // Add revenue data
    orderData.forEach(order => {
      try {
        const orderDate = new Date(order.created_at);
        if (orderDate >= startDate) {
          let dateStr;
          
          if (revenueTimeScale === 'daily') {
            dateStr = orderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (revenueTimeScale === 'weekly') {
            const weekNumber = Math.ceil((orderDate.getDate() + orderDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${orderDate.toLocaleString('default', { month: 'short' })} ${orderDate.getFullYear()}`;
          }
          
          if (periodRevenue[dateStr] !== undefined) {
            periodRevenue[dateStr] += order.total_price;
          }
        }
      } catch (err) {
        console.error('Error processing order date:', err);
      }
    });

    setRevenueChartData({
      categories: Object.keys(periodRevenue),
      series: [{
        name: 'Revenue',
        data: Object.values(periodRevenue)
      }]
    });
  };

  const processUserActivityChartData = () => {
    // Time scale calculations based on selected scale
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(userActivityTimeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Initialize time periods
    const periods = {};
    
    if (userActivityTimeScale === 'daily') {
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        periods[dateStr] = 0;
      }
    } else if (userActivityTimeScale === 'weekly') {
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        periods[dateStr] = 0;
      }
    } else {
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        periods[monthYear] = 0;
      }
    }

    // User activity 
    const newUsers = {...periods};
    const activeUsers = {...periods};

    // Reset values to 0
    Object.keys(newUsers).forEach(key => {
      newUsers[key] = 0;
      activeUsers[key] = 0;
    });

    // Count new users by join date
    userData.forEach(user => {
      if (user.created_at) {
        try {
          const userDate = new Date(user.created_at);
          if (userDate >= startDate) {
            let dateStr;
            
            if (userActivityTimeScale === 'daily') {
              dateStr = userDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            } else if (userActivityTimeScale === 'weekly') {
              const weekNumber = Math.ceil((userDate.getDate() + userDate.getDay()) / 7);
              dateStr = `Week ${weekNumber}`;
            } else {
              dateStr = `${userDate.toLocaleString('default', { month: 'short' })} ${userDate.getFullYear()}`;
            }
            
            if (newUsers[dateStr] !== undefined) {
              newUsers[dateStr]++;
            }
          }
        } catch (err) {
          console.error('Error processing user date:', err);
        }
      }
    });

    // Count active users by transaction date
    transactionData.forEach(txn => {
      try {
        const txnDate = new Date(txn.time);
        if (txnDate >= startDate) {
          let dateStr;
          
          if (userActivityTimeScale === 'daily') {
            dateStr = txnDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (userActivityTimeScale === 'weekly') {
            const weekNumber = Math.ceil((txnDate.getDate() + txnDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${txnDate.toLocaleString('default', { month: 'short' })} ${txnDate.getFullYear()}`;
          }
          
          if (activeUsers[dateStr] !== undefined) {
            // Only count unique users
            activeUsers[dateStr] = Math.max(activeUsers[dateStr], 1);
          }
        }
      } catch (err) {
        console.error('Error processing transaction date:', err);
      }
    });

    setUserActivityChartData({
      categories: Object.keys(newUsers),
      series: [
        {
          name: 'New Users',
          data: Object.values(newUsers)
        },
        {
          name: 'Active Users',
          data: Object.values(activeUsers)
        }
      ]
    });
  };

  const processContentGenerationChartData = () => {
    // Time scale calculations based on selected scale
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(contentTimeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Content generation
    const contentGeneration = {};
    
    if (contentTimeScale === 'daily') {
      // Initialize last X days
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        contentGeneration[dateStr] = { images: 0, audios: 0, videos: 0 };
      }
    } else if (contentTimeScale === 'weekly') {
      // Initialize last X weeks
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        contentGeneration[dateStr] = { images: 0, audios: 0, videos: 0 };
      }
    } else {
      // Monthly (default)
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        contentGeneration[monthYear] = { images: 0, audios: 0, videos: 0 };
      }
    }

    // Count images by time period
    generatedImageData.forEach(image => {
      try {
        const imageDate = new Date(image.created_at);
        if (imageDate >= startDate) {
          let dateStr;
          
          if (contentTimeScale === 'daily') {
            dateStr = imageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (contentTimeScale === 'weekly') {
            const weekNumber = Math.ceil((imageDate.getDate() + imageDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${imageDate.toLocaleString('default', { month: 'short' })} ${imageDate.getFullYear()}`;
          }
          
          if (contentGeneration[dateStr] !== undefined) {
            contentGeneration[dateStr].images++;
          }
        }
      } catch (err) {
        console.error('Error processing image date:', err);
      }
    });

    // Count audios by time period
    convertedAudioData.forEach(audio => {
      try {
        const audioDate = new Date(audio.uploaded_at);
        if (audioDate >= startDate) {
          let dateStr;
          
          if (contentTimeScale === 'daily') {
            dateStr = audioDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (contentTimeScale === 'weekly') {
            const weekNumber = Math.ceil((audioDate.getDate() + audioDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${audioDate.toLocaleString('default', { month: 'short' })} ${audioDate.getFullYear()}`;
          }
          
          if (contentGeneration[dateStr] !== undefined) {
            contentGeneration[dateStr].audios++;
          }
        }
      } catch (err) {
        console.error('Error processing audio date:', err);
      }
    });

    // Count videos by time period
    generatedVideoData.forEach(video => {
      try {
        const videoDate = new Date(video.created_at);
        if (videoDate >= startDate) {
          let dateStr;
          
          if (contentTimeScale === 'daily') {
            dateStr = videoDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (contentTimeScale === 'weekly') {
            const weekNumber = Math.ceil((videoDate.getDate() + videoDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${videoDate.toLocaleString('default', { month: 'short' })} ${videoDate.getFullYear()}`;
          }
          
          if (contentGeneration[dateStr] !== undefined) {
            contentGeneration[dateStr].videos++;
          }
        }
      } catch (err) {
        console.error('Error processing video date:', err);
      }
    });

    setContentGenerationData({
      categories: Object.keys(contentGeneration),
      series: [
        {
          name: 'Images',
          data: Object.values(contentGeneration).map(item => item.images)
        },
        {
          name: 'Audios',
          data: Object.values(contentGeneration).map(item => item.audios)
        },
        {
          name: 'Videos',
          data: Object.values(contentGeneration).map(item => item.videos)
        }
      ]
    });
  };

  const prepareRecentUsers = () => {
    // Sort users by creation date (desc) and take the first 5
    const sortedUsers = [...userData].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA;
    }).slice(0, 5);

    setRecentUsers(sortedUsers);
  };

  // Chart options
  const areaChartOptions = {
    chart: {
      type: 'area',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: ['var(--primary-color)'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    markers: {
      size: 4,
      strokeWidth: 0,
      hover: {
        size: 6
      }
    },
    xaxis: {
      categories: revenueChartData.categories,
      labels: {
        style: {
          fontSize: '12px',
          colors: 'var(--text-tertiary)'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Revenue ($)',
        style: {
          fontSize: '14px',
          color: 'var(--text-secondary)'
        }
      },
      labels: {
        formatter: (value) => { return '$' + value.toFixed(0) }
      }
    },
    grid: {
      borderColor: 'var(--border-color)',
      row: {
        colors: ['transparent', 'transparent'],
        opacity: 0.5
      },
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (value) => { return '$' + value.toFixed(2) }
      }
    }
  };

  const barOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      stacked: false
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 4
      },
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)'],
    xaxis: {
      categories: userActivityChartData.categories,
      labels: {
        style: {
          fontSize: '12px',
          colors: 'var(--text-tertiary)'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Users',
        style: {
          fontSize: '14px',
          color: 'var(--text-secondary)'
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right'
    },
    grid: {
      borderColor: 'var(--border-color)',
      row: {
        colors: ['transparent', 'transparent'],
        opacity: 0.5
      },
    },
    tooltip: {
      theme: 'light'
    }
  };

  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom',
      fontSize: '14px',
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true,
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600,
              formatter: function (val) {
                return val;
              }
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          height: 250,
        },
        legend: {
          show: false
        }
      }
    }]
  };

  const transactionDonutOptions = {
    ...donutOptions,
    colors: ['var(--primary-color)', 'var(--secondary-color)', 'var(--accent-color)', 'var(--warning-color)', '#e11d48'],
  };

  // System health stats
  const systemStats = [
    {
      title: 'System Health',
      value: 98,
      color: 'var(--success-color)',
    },
    {
      title: 'API Response',
      value: 254,
      color: 'var(--info-color)',
      suffix: 'ms',
    },
    {
      title: 'Server Load',
      value: 42,
      color: 'var(--success-color)',
      suffix: '%',
    },
  ];

  // Add CSS for the DataStateDisplay component
  const dataLoadingContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    minHeight: '50vh',
  };

  const dataErrorContainerStyle = {
    padding: '24px',
    maxWidth: '800px',
    margin: '48px auto',
  };

  // Add a component to display loading and error states
  const DataStateDisplay = ({ loading, error, children }) => {
    if (loading) {
      return (
        <div style={dataLoadingContainerStyle}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading dashboard data...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div style={dataErrorContainerStyle}>
          <Alert
            message="Data Loading Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => window.location.reload()}
                type="primary"
              >
                Retry
              </Button>
            }
          />
        </div>
      );
    }
    
    return children;
  };

  return (
    <div className="dashboard-container">
      <DataStateDisplay loading={loading} error={error}>
        <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
          {/* Dashboard Header */}
          <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: 'bold',
                color: 'var(--primary-color)',
                marginBottom: '8px'
              }}>
                Matrix Admin Dashboard
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
                Welcome back! Here's an overview of your platform's performance
              </p>
            </div>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => {
                setLoading(true);
                const fetchAllData = async () => {
                  setError(null);
                  try {
                    const results = await Promise.allSettled([
                      fetchUsers(),
                      fetchOrders(),
                      fetchTransactions(),
                      fetchGeneratedImages(),
                      fetchConvertedAudios(),
                      fetchGeneratedVideos(),
                      fetchRecentFeedback(),
                      fetchRecentIssues()
                    ]);
                    
                    const criticalFailures = results.slice(0, 3).filter(r => r.status === 'rejected');
                    if (criticalFailures.length > 0) {
                      console.warn('Some critical data fetching operations failed:', 
                        criticalFailures.map(f => f.reason?.message || 'Unknown error'));
                      setError('Some dashboard data could not be loaded. Please refresh to try again.');
                    }
                    
                    setLoading(false);
                  } catch (err) {
                    console.error('Error fetching dashboard data:', err);
                    setError('Failed to load dashboard data. Please try again later.');
                    setLoading(false);
                  }
                };
                fetchAllData();
              }}
            >
              Refresh Data
            </Button>
          </div>

          {/* Quick Stats */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <div className="stat-card primary">
                <div>
                  <div className="stat-value">{dashboardMetrics.totalUsers}</div>
                  <div className="stat-label">Registered Users</div>
                </div>
                <UserOutlined className="stat-icon" />
              </div>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <div className="stat-card secondary">
                <div>
                  <div className="stat-value">{dashboardMetrics.activeSubscriptions}</div>
                  <div className="stat-label">Active Subscriptions</div>
                </div>
                <FileProtectOutlined className="stat-icon" />
              </div>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <div className="stat-card accent">
                <div>
                  <div className="stat-value">${dashboardMetrics.totalRevenue.toLocaleString()}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
                <DollarOutlined className="stat-icon" />
              </div>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <div className="stat-card warning">
                <div>
                  <div className="stat-value">{dashboardMetrics.totalTransactions}</div>
                  <div className="stat-label">Total Transactions</div>
                </div>
                <TransactionOutlined className="stat-icon" />
              </div>
            </Col>
          </Row>
          
          {/* Additional stats for generated images, converted audios, and generated videos */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={8}>
              <div className="stat-card primary">
                <div>
                  <div className="stat-value">{dashboardMetrics.totalImages}</div>
                  <div className="stat-label">Generated Images</div>
                </div>
                <FileImageOutlined className="stat-icon" />
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <div className="stat-card secondary">
                <div>
                  <div className="stat-value">{dashboardMetrics.totalAudios}</div>
                  <div className="stat-label">Converted Audios</div>
                </div>
                <AudioOutlined className="stat-icon" />
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <div className="stat-card accent">
                <div>
                  <div className="stat-value">{dashboardMetrics.totalVideos}</div>
                  <div className="stat-label">Generated Videos</div>
                </div>
                <VideoCameraOutlined className="stat-icon" />
              </div>
            </Col>
          </Row>

          {/* Rest of the dashboard content */}
          {/* Revenue Chart - Separate row to fix layout issues */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24}>
              <Card 
                title="Revenue Overview" 
                className="dashboard-card"
                extra={
                  <Radio.Group 
                    value={revenueTimeScale}
                    onChange={e => {
                      setRevenueTimeScale(e.target.value);
                    }}
                  >
                    <Radio.Button value="daily">Daily</Radio.Button>
                    <Radio.Button value="weekly">Weekly</Radio.Button>
                    <Radio.Button value="monthly">Monthly</Radio.Button>
                  </Radio.Group>
                }
              >
                <div style={{ height: '350px', width: '100%' }}>  {/* Fixed height container */}
                  {revenueChartData.categories.length > 0 ? (
                    <Chart
                      options={{
                        chart: {
                          type: 'area',
                          toolbar: { show: false },
                          width: '100%',
                          height: 350
                        },
                        dataLabels: { enabled: false },
                        stroke: { curve: 'smooth' },
                        xaxis: { 
                          categories: revenueChartData.categories,
                          labels: {
                            rotate: -45,
                            style: { fontSize: '12px' }
                          }
                        },
                        tooltip: { x: { format: 'dd/MM/yy' } },
                        grid: {
                          padding: {
                            left: 10,
                            right: 10
                          }
                        }
                      }}
                      series={revenueChartData.series}
                      type="area"
                      height={300}
                      width="100%"
                    />
                  ) : (
                    <Empty description="No revenue data available" />
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          {/* User Activity Chart - Separate row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24}>
              <Card 
                title="User Activity" 
                className="dashboard-card"
                extra={
                  <Radio.Group 
                    value={userActivityTimeScale}
                    onChange={e => {
                      setUserActivityTimeScale(e.target.value);
                    }}
                    size="middle"
                  >
                    <Radio.Button value="daily">Daily</Radio.Button>
                    <Radio.Button value="weekly">Weekly</Radio.Button>
                    <Radio.Button value="monthly">Monthly</Radio.Button>
                  </Radio.Group>
                }
              >
                <div style={{ height: '350px', width: '100%' }}>  {/* Fixed height container */}
                  {userActivityChartData.categories.length > 0 ? (
                    <Chart
                      options={{
                        chart: {
                          type: 'bar',
                          stacked: true,
                          toolbar: { show: false },
                          width: '100%',
                          height: 350
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                          },
                        },
                        dataLabels: { enabled: false },
                        xaxis: { 
                          categories: userActivityChartData.categories,
                          labels: { 
                            rotate: -45,
                            style: { fontSize: '12px' }
                          }
                        },
                        grid: {
                          padding: {
                            left: 10,
                            right: 10
                          }
                        }
                      }}
                      series={userActivityChartData.series}
                      type="bar"
                      height={300}
                      width="100%"
                    />
                  ) : (
                    <Empty description="No user activity data available" />
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          {/* Distribution Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12}>
              <Card title="Subscription Distribution" className="chart-card">
                <div style={{ height: '300px', width: '100%' }}>  {/* Fixed height container */}
                  <Chart
                    options={{
                      ...donutOptions,
                      labels: subscriptionDistributionData.labels,
                      chart: {
                        ...donutOptions.chart,
                        width: '100%'
                      }
                    }}
                    series={subscriptionDistributionData.series}
                    type="donut"
                    height={280}
                    width="100%"
                  />
                </div>
              </Card>
            </Col>
            
            <Col xs={24} sm={12}>
              <Card title="Transaction Types" className="chart-card">
                <div style={{ height: '300px', width: '100%' }}>  {/* Fixed height container */}
                  <Chart
                    options={{
                      ...transactionDonutOptions,
                      labels: transactionTypeData.labels,
                      chart: {
                        ...transactionDonutOptions.chart,
                        width: '100%'
                      }
                    }}
                    series={transactionTypeData.series}
                    type="donut"
                    height={280}
                    width="100%"
                  />
                </div>
              </Card>
            </Col>
          </Row>
          
          {/* Content Generation Chart */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24}>
              <Card 
                title="Content Generation" 
                className="dashboard-card"
                extra={
                  <Radio.Group 
                    value={contentTimeScale}
                    onChange={e => {
                      setContentTimeScale(e.target.value);
                    }}
                  >
                    <Radio.Button value="daily">Daily</Radio.Button>
                    <Radio.Button value="weekly">Weekly</Radio.Button>
                    <Radio.Button value="monthly">Monthly</Radio.Button>
                  </Radio.Group>
                }
              >
                <div style={{ height: '350px', width: '100%' }}>  {/* Fixed height container */}
                  {contentGenerationData.categories.length > 0 ? (
                    <Chart
                      options={{
                        chart: {
                          type: 'line',
                          toolbar: { show: false },
                          width: '100%',
                          height: 350
                        },
                        dataLabels: { enabled: false },
                        stroke: { curve: 'smooth', width: 2 },
                        xaxis: { 
                          categories: contentGenerationData.categories,
                          labels: { 
                            rotate: -45,
                            style: { fontSize: '12px' }
                          }
                        },
                        tooltip: { y: { formatter: (val) => `${val} items` } },
                        grid: {
                          padding: {
                            left: 10,
                            right: 10
                          }
                        }
                      }}
                      series={contentGenerationData.series}
                      type="line"
                      height={300}
                      width="100%"
                    />
                  ) : (
                    <Empty description="No content generation data available" />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
          
          {/* Recent Users and Activity Row */}
          <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
            <Col xs={24} lg={12}>
              <Card 
                title="Recent Feedback"
                className="dashboard-card"
                extra={
                  <Link to="/feedback">
                    <Button type="text" icon={<SearchOutlined />}>View All</Button>
                  </Link>
                }
              >
                {recentFeedback.length > 0 ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={recentFeedback}
                    renderItem={item => (
                      <List.Item
                        actions={[
                          item.status !== 'resolved' && (
                            <Button 
                              type="primary" 
                              size="small"
                              onClick={() => showResolveModal(item, 'feedback')}
                            >
                              Resolve
                            </Button>
                          ),
                          item.status === 'resolved' && (
                            <Tag color="green">Resolved</Tag>
                          )
                        ].filter(Boolean)}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar 
                              src={item.user?.avatar}
                              icon={!item.user?.avatar && <UserOutlined />}
                              style={{ backgroundColor: !item.user?.avatar ? '#1677ff' : undefined }}
                            />
                          }
                          title={
                            <div>
                              <span style={{ fontWeight: '500' }}>{item.user?.name || 'Unknown User'}</span>
                              <Tag color={item.issue_type === 'Great experience and helpful' ? 'green' : 'blue'} style={{ marginLeft: '8px' }}>
                                {item.issue_type || 'Feedback'}
                              </Tag>
                            </div>
                          }
                          description={
                            <div>
                              <div style={{ 
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '300px'
                              }}>
                                {item.description || 'No description provided'}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                                {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty description="No feedback data available" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card 
                title="Recent Issues"
                className="dashboard-card"
                extra={
                  <Link to="/issues">
                    <Button type="text" icon={<SearchOutlined />}>View All</Button>
                  </Link>
                }
              >
                {recentIssues.length > 0 ? (
                  <List
                    itemLayout="horizontal"
                    dataSource={recentIssues}
                    renderItem={item => (
                      <List.Item
                        actions={[
                          item.status !== 'resolved' && (
                            <Button 
                              type="primary" 
                              size="small"
                              onClick={() => showResolveModal(item, 'issue')}
                            >
                              Resolve
                            </Button>
                          ),
                          item.status === 'resolved' ? (
                            <Tag color="green">Resolved</Tag>
                          ) : (
                            <Tag color="error">Pending</Tag>
                          )
                        ].filter(Boolean)}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar 
                              src={item.user?.avatar}
                              icon={!item.user?.avatar && <UserOutlined />}
                              style={{ backgroundColor: !item.user?.avatar ? '#f5222d' : undefined }}
                            />
                          }
                          title={
                            <div>
                              <span style={{ fontWeight: '500' }}>{item.user?.name || 'Unknown User'}</span>
                              <Tag color="orange" style={{ marginLeft: '8px' }}>
                                {item.issue_type || 'Help Request'}
                              </Tag>
                            </div>
                          }
                          description={
                            <div>
                              <div style={{ 
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '300px'
                              }}>
                                {item.description || 'No description provided'} 
                                <span style={{ fontWeight: '500' }}>{item.order_id ? ` • Order #${item.order_id}` : ''}</span>
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                                {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty description="No issues data available" />
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </DataStateDisplay>
      
      {/* Resolve Modal */}
      <Modal
        title={`Resolve ${resolveType === 'feedback' ? 'Feedback' : 'Issue'}`}
        open={resolveModalVisible}
        onOk={handleResolveSubmit}
        onCancel={handleResolveCancel}
        okText="Resolve"
        cancelText="Cancel"
      >
        {selectedItem && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>User:</strong> {selectedItem.user?.name || 'Unknown'} ({selectedItem.user?.email || 'No email'})
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Created:</strong> {new Date(selectedItem.created_at).toLocaleString()}
            </div>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {resolveType === 'feedback' ? 'Feedback' : 'Issue'} Details:
              </div>
              <div><strong>Type:</strong> {selectedItem.issue_type}</div>
              <div><strong>Description:</strong> {selectedItem.description || 'No description provided'}</div>
              {selectedItem.order_id && (
                <div><strong>Order ID:</strong> {selectedItem.order_id}</div>
              )}
            </div>
            <div>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Admin Comment:
              </label>
              <Input.TextArea
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder="Enter your comment for resolving this item..."
                rows={4}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default DashboardPage;
